/**
 * @fileoverview Primitives Section - Component Library
 *
 * Purpose: Display all primitive UI components (buttons, inputs, switches, etc.)
 * Used by: Component library main page
 */

import { ComponentShowcase } from "./-ui.component-showcase";
import { ComponentHeader } from "./-ui.component-header";
import { VariantGrid, VariantItem } from "./-ui.variant-grid";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Alert, AlertTitle, AlertDescription } from "@/ui/primitives/alert";
import { AspectRatio } from "@/ui/primitives/aspect-ratio";
import { Avatar, AvatarImage, AvatarFallback } from "@/ui/primitives/avatar";
import { Badge } from "@/ui/primitives/badge";
import { Tag } from "@/ui/primitives/tag";
import { Separator } from "@/ui/primitives/separator";
import {
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
} from "@/ui/primitives/button-group";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/ui/primitives/card";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupButton,
} from "@/ui/primitives/input-group";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/ui/primitives/empty";
import {
  Item,
  ItemGroup,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
  ItemSeparator,
} from "@/ui/primitives/item";
import { Kbd, KbdGroup } from "@/ui/primitives/kbd";
import { Progress } from "@/ui/primitives/progress";
import { NativeSelect } from "@/ui/primitives/native-select";
import { RadioGroup, RadioGroupItem } from "@/ui/primitives/radio-group";
import { Skeleton } from "@/ui/primitives/skeleton";
import { Slider } from "@/ui/primitives/slider";
import { ScrollArea, ScrollBar } from "@/ui/primitives/scroll-area";
import { Spinner } from "@/ui/primitives/spinner";
import { Toggle } from "@/ui/primitives/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/ui/primitives/toggle-group";
import {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
} from "@/ui/primitives/field";
import {
  Loader2,
  Mail,
  Search,
  ArrowRight,
  User,
  AlertCircle,
  Terminal,
  CheckCircle2,
  X,
  Bell,
  ShoppingCart,
  Zap,
  Star,
  Inbox,
  FileQuestion,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  FileText,
  Tag as Tag2,
} from "lucide-react";
import { useState } from "react";

// Helper component for dismissible alerts
function DismissibleAlertExamples() {
  const [showInfo, setShowInfo] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  return (
    <div className="space-y-4">
      {/* Trigger Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => setShowInfo(true)}
          disabled={showInfo}
        >
          Show Info Alert
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowError(true)}
          disabled={showError}
        >
          Show Error Alert
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowSuccess(true)}
          disabled={showSuccess}
        >
          Show Success Alert
        </Button>
      </div>

      {/* Dismissible Alerts */}
      {showInfo && (
        <Alert>
          <Terminal className="h-4 w-4" />
          <AlertTitle>New Feature Available</AlertTitle>
          <AlertDescription>
            Check out our new dashboard analytics. Click the X to dismiss.
          </AlertDescription>
          <button
            onClick={() => setShowInfo(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </Alert>
      )}

      {showError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Action Required</AlertTitle>
          <AlertDescription>
            Your payment method needs to be updated. Click X to dismiss.
          </AlertDescription>
          <button
            onClick={() => setShowError(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </Alert>
      )}

      {showSuccess && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>All set!</AlertTitle>
          <AlertDescription>
            Your account is fully configured. Click X to dismiss.
          </AlertDescription>
          <button
            onClick={() => setShowSuccess(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </Alert>
      )}
    </div>
  );
}

// Helper component for form with alert
function FormWithAlertExample() {
  const [showError, setShowError] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = () => {
    if (!email || !email.includes("@")) {
      setShowError(true);
    } else {
      setShowError(false);
      alert("Form submitted successfully!");
    }
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      {showError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Submission Failed</AlertTitle>
          <AlertDescription>
            Please fix the errors below and try again.
          </AlertDescription>
          <button
            onClick={() => setShowError(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      <div className="space-y-2">
        <label className="text-sm font-medium">Email</label>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={
            showError && (!email || !email.includes("@"))
              ? "border-destructive"
              : ""
          }
        />
        {showError && (!email || !email.includes("@")) && (
          <p className="text-sm text-destructive">Valid email is required</p>
        )}
      </div>
      <Button onClick={handleSubmit}>Submit Form</Button>
    </div>
  );
}

// Helper component for success alert demo
function SuccessAlertExample() {
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSave = () => {
    setShowSuccess(true);
    // Auto-dismiss after 3 seconds
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      {showSuccess && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Changes saved!</AlertTitle>
          <AlertDescription>
            Your preferences have been updated successfully. (Auto-dismisses in
            3s)
          </AlertDescription>
          <button
            onClick={() => setShowSuccess(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Email Notifications</label>
          <Switch />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Marketing Emails</label>
          <Switch />
        </div>
      </div>
      <Button onClick={handleSave}>Save Preferences</Button>
    </div>
  );
}

export function PrimitivesSection() {
  return (
    <div id="components" className="scroll-mt-24 space-y-12">
      <div>
        <h1 className="mb-2 text-4xl font-bold tracking-tight">Components</h1>
        <p className="text-lg text-muted-foreground">
          All UI components built with shadcn/ui, showcasing variants, sizes,
          and states.
        </p>
      </div>

      {/* Buttons */}
      <div id="buttons" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Buttons"
          description="Accessible button component with multiple variants and sizes using CVA."
          filePath="src/ui/primitives/button.tsx"
        />

        {/* Button Variants */}
        <ComponentShowcase
          title="Variants"
          description="Different visual styles for various contexts"
        >
          <VariantGrid>
            <VariantItem label="Default">
              <Button>Button CTA</Button>
            </VariantItem>
            <VariantItem label="Destructive">
              <Button variant="destructive">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Outline">
              <Button variant="outline">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Secondary">
              <Button variant="secondary">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Ghost">
              <Button variant="ghost">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Link">
              <Button variant="link">Button CTA</Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        {/* Button Sizes */}
        <ComponentShowcase
          title="Sizes"
          description="Different sizes for hierarchy and spacing"
        >
          <VariantGrid columns={4}>
            <VariantItem label="Small">
              <Button size="sm">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Default">
              <Button>Button CTA</Button>
            </VariantItem>
            <VariantItem label="Large">
              <Button size="lg">Button CTA</Button>
            </VariantItem>
            <VariantItem label="Icon">
              <Button size="icon">
                <Mail className="h-4 w-4" />
              </Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        {/* Button States */}
        <ComponentShowcase
          title="States"
          description="Interactive and disabled states"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Default State">
              <Button>Button CTA</Button>
            </VariantItem>
            <VariantItem label="Disabled">
              <Button disabled>Button CTA</Button>
            </VariantItem>
            <VariantItem label="Loading">
              <Button disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        {/* Button with Icons */}
        <ComponentShowcase
          title="With Icons"
          description="Buttons with leading or trailing icons"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Icon Left">
              <Button>
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </Button>
            </VariantItem>
            <VariantItem label="Icon Right">
              <Button>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </VariantItem>
            <VariantItem label="Icon Only">
              <Button size="icon" variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        {/* Button with Icon Expansion */}
        <ComponentShowcase
          title="Icon Expansion on Hover"
          description="Add expandIcon prop to animate icons to 125% on hover"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Icon Left (Expand)">
              <Button expandIcon>
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </Button>
            </VariantItem>
            <VariantItem label="Icon Right (Expand)">
              <Button expandIcon>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </VariantItem>
            <VariantItem label="Icon Only (Expand)">
              <Button size="icon" variant="outline" expandIcon>
                <Search className="h-4 w-4" />
              </Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Inputs */}
      <div id="inputs" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Inputs"
          description="Form input fields with consistent styling and accessibility."
          filePath="src/ui/primitives/input.tsx"
        />

        <ComponentShowcase
          title="Input Types"
          description="Different input types and states"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Text Input</label>
              <Input type="text" placeholder="Enter your name" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email Input</label>
              <Input type="email" placeholder="email@example.com" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Password Input</label>
              <Input type="password" placeholder="••••••••" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Disabled Input</label>
              <Input type="text" placeholder="Disabled" disabled />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">File Input</label>
              <Input type="file" />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Switches */}
      <div id="switches" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Switches"
          description="Toggle switches for boolean settings and preferences."
          filePath="src/ui/primitives/switch.tsx"
        />

        <ComponentShowcase
          title="Switch States"
          description="Toggle switches in different states"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Unchecked">
              <Switch />
            </VariantItem>
            <VariantItem label="Checked">
              <Switch checked />
            </VariantItem>
            <VariantItem label="Disabled">
              <Switch disabled />
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="With Labels"
          description="Switches with descriptive labels"
        >
          <div className="max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <label className="text-sm font-medium">
                  Email Notifications
                </label>
                <p className="text-sm text-muted-foreground">
                  Receive emails about your account activity
                </p>
              </div>
              <Switch />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <label className="text-sm font-medium">Marketing Emails</label>
                <p className="text-sm text-muted-foreground">
                  Receive emails about new products and features
                </p>
              </div>
              <Switch />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Selects */}
      <div id="selects" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Selects"
          description="Dropdown select components with keyboard navigation."
          filePath="src/ui/primitives/select.tsx"
        />

        <ComponentShowcase
          title="Select Examples"
          description="Select dropdowns with different configurations"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Simple Select</label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select a fruit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apple">Apple</SelectItem>
                  <SelectItem value="banana">Banana</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="grape">Grape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">With Default Value</label>
              <Select defaultValue="banana">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apple">Apple</SelectItem>
                  <SelectItem value="banana">Banana</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Disabled Select</label>
              <Select disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Disabled" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apple">Apple</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Alerts */}
      <div id="alerts" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Alerts"
          description="Feedback alerts for displaying important messages and notifications."
          filePath="src/ui/primitives/alert.tsx"
        />

        <ComponentShowcase
          title="Alert Variants"
          description="Different alert styles for various message types (static display)"
        >
          <div className="space-y-4">
            <Alert>
              <Terminal className="h-4 w-4" />
              <AlertTitle>Heads up!</AlertTitle>
              <AlertDescription>
                You can add components to your app using the cli.
              </AlertDescription>
            </Alert>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Your session has expired. Please log in again.
              </AlertDescription>
            </Alert>

            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>
                Your profile has been updated successfully.
              </AlertDescription>
            </Alert>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Dismissible Alerts"
          description="Click buttons to trigger alerts, then click X to dismiss"
        >
          <DismissibleAlertExamples />
        </ComponentShowcase>

        <ComponentShowcase
          title="In Context - Form Validation"
          description="Try submitting with invalid email to see the error alert"
        >
          <FormWithAlertExample />
        </ComponentShowcase>

        <ComponentShowcase
          title="In Context - Success Feedback"
          description="Click 'Save Preferences' to see success alert (auto-dismisses after 3 seconds)"
        >
          <SuccessAlertExample />
        </ComponentShowcase>
      </div>

      {/* Avatars */}
      <div id="avatars" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Avatars"
          description="User avatar component with image fallback support."
          filePath="src/ui/primitives/avatar.tsx"
        />

        <ComponentShowcase
          title="Avatar Sizes"
          description="Avatars in different sizes"
        >
          <VariantGrid columns={4}>
            <VariantItem label="Small (32px)">
              <Avatar className="h-8 w-8">
                <AvatarImage src="https://github.com/shadcn.png" alt="User" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
            </VariantItem>
            <VariantItem label="Default (40px)">
              <Avatar>
                <AvatarImage src="https://github.com/shadcn.png" alt="User" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
            </VariantItem>
            <VariantItem label="Large (64px)">
              <Avatar className="h-16 w-16">
                <AvatarImage src="https://github.com/shadcn.png" alt="User" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
            </VariantItem>
            <VariantItem label="Extra Large (96px)">
              <Avatar className="h-24 w-24">
                <AvatarImage src="https://github.com/shadcn.png" alt="User" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Avatar with Fallback"
          description="Shows fallback when image fails to load"
        >
          <VariantGrid columns={3}>
            <VariantItem label="With Image">
              <Avatar>
                <AvatarImage src="https://github.com/shadcn.png" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
            </VariantItem>
            <VariantItem label="Fallback (Initials)">
              <Avatar>
                <AvatarImage src="/broken-image.jpg" />
                <AvatarFallback>JD</AvatarFallback>
              </Avatar>
            </VariantItem>
            <VariantItem label="Fallback (Icon)">
              <Avatar>
                <AvatarImage src="/broken-image.jpg" />
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Badges */}
      <div id="badges" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Badges"
          description="Small status indicators, labels, and notification counts. Supports color system, sizes, and multiple variants."
          filePath="src/ui/primitives/badge.tsx"
        />

        <ComponentShowcase
          title="Legacy Variants"
          description="Original badge variants (backward compatible)"
        >
          <VariantGrid columns={4}>
            <VariantItem label="Default">
              <Badge>Default</Badge>
            </VariantItem>
            <VariantItem label="Secondary">
              <Badge variant="secondary">Secondary</Badge>
            </VariantItem>
            <VariantItem label="Destructive">
              <Badge variant="destructive">Destructive</Badge>
            </VariantItem>
            <VariantItem label="Outline">
              <Badge variant="outline">Outline</Badge>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Color × Variant Matrix"
          description="13 colors across soft, outline, and solid variants"
        >
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 px-1 text-[10px] font-medium text-muted-foreground">
              <span>Color</span>
              <span>Soft</span>
              <span>Outline</span>
              <span>Solid</span>
            </div>
            {(
              [
                "gray",
                "blue",
                "red",
                "green",
                "emerald",
                "amber",
                "teal",
                "indigo",
                "purple",
                "pink",
                "orange",
                "violet",
                "sky",
              ] as const
            ).map((color) => (
              <div key={color} className="grid grid-cols-4 items-center gap-2">
                <span className="text-xs capitalize text-muted-foreground">
                  {color}
                </span>
                <Badge color={color} variant="soft">
                  {color}
                </Badge>
                <Badge color={color} variant="outline">
                  {color}
                </Badge>
                <Badge color={color} variant="solid">
                  {color}
                </Badge>
              </div>
            ))}
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Sizes"
          description="sm, md (default), and lg sizes"
        >
          <div className="flex items-center gap-4">
            <VariantItem label="Small">
              <Badge color="blue" variant="soft" size="sm">
                Small
              </Badge>
            </VariantItem>
            <VariantItem label="Medium (default)">
              <Badge color="blue" variant="soft">
                Medium
              </Badge>
            </VariantItem>
            <VariantItem label="Large">
              <Badge color="blue" variant="soft" size="lg">
                Large
              </Badge>
            </VariantItem>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Usage Examples"
          description="Badges in practical contexts"
        >
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3">
              <Badge color="emerald" variant="soft" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Ready
              </Badge>
              <Badge color="blue" variant="soft" className="gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Running
              </Badge>
              <Badge color="red" variant="solid">
                Failed
              </Badge>
              <Badge color="amber" variant="soft" size="sm">
                Warning
              </Badge>
              <Badge variant="outline">v2.1.0</Badge>
            </div>

            <div className="flex gap-4">
              <div className="relative inline-block">
                <Button variant="outline" size="icon">
                  <Bell className="h-4 w-4" />
                </Button>
                <Badge className="absolute -right-2 -top-2 min-w-5 justify-center px-1.5">
                  5
                </Badge>
              </div>
              <div className="relative inline-block">
                <Button variant="outline" size="icon">
                  <ShoppingCart className="h-4 w-4" />
                </Button>
                <Badge
                  variant="destructive"
                  className="absolute -right-2 -top-2 min-w-5 justify-center px-1.5"
                >
                  12
                </Badge>
              </div>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Tags */}
      <div id="tags" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Tags"
          description="Compact labels with optional leading content, dot indicators, and remove actions."
          filePath="src/ui/primitives/tag.tsx"
        />

        <ComponentShowcase
          title="Basic Tags"
          description="Plain tags in different sizes"
        >
          <div className="flex items-center gap-3">
            <Tag size="sm">Small</Tag>
            <Tag>Default</Tag>
            <Tag size="lg">Large</Tag>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Tags with Dot"
          description="Status indicators with colored dots"
        >
          <div className="flex flex-wrap gap-3">
            <Tag dot>Default dot</Tag>
            <Tag dot="emerald">Online</Tag>
            <Tag dot="red">Offline</Tag>
            <Tag dot="amber">Away</Tag>
            <Tag dot="blue">Active</Tag>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Tags with Leading Content"
          description="Icons or avatars before the label"
        >
          <div className="flex flex-wrap gap-3">
            <Tag leading={<FileText className="h-3 w-3" />}>Document</Tag>
            <Tag leading={<Star className="h-3 w-3" />}>Favorite</Tag>
            <Tag leading={<Tag2 className="h-3 w-3" />}>Category</Tag>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Removable Tags"
          description="Tags with an X button for removal"
        >
          <div className="flex flex-wrap gap-3">
            <Tag onRemove={() => {}}>Removable</Tag>
            <Tag dot="emerald" onRemove={() => {}}>
              Active Filter
            </Tag>
            <Tag leading={<Star className="h-3 w-3" />} onRemove={() => {}}>
              Starred
            </Tag>
          </div>
        </ComponentShowcase>
      </div>

      {/* Separator */}
      <div id="separator" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Separator"
          description="Visual separator for dividing content sections."
          filePath="src/ui/primitives/separator.tsx"
        />

        <ComponentShowcase
          title="Separator Examples"
          description="Horizontal and vertical separators"
        >
          <div className="space-y-8">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Horizontal Separator
              </p>
              <div className="space-y-4">
                <p className="text-sm">Content above separator</p>
                <Separator />
                <p className="text-sm">Content below separator</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Vertical Separator
              </p>
              <div className="flex h-8 items-center gap-4">
                <span className="text-sm">Item 1</span>
                <Separator orientation="vertical" />
                <span className="text-sm">Item 2</span>
                <Separator orientation="vertical" />
                <span className="text-sm">Item 3</span>
              </div>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Button Group */}
      <div id="button-group" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Button Group"
          description="Group related buttons together with connected borders."
          filePath="src/ui/primitives/button-group.tsx"
        />

        <ComponentShowcase
          title="Horizontal Button Groups"
          description="Buttons grouped side-by-side"
        >
          <div className="space-y-6">
            <ButtonGroup>
              <Button variant="outline">Left</Button>
              <Button variant="outline">Center</Button>
              <Button variant="outline">Right</Button>
            </ButtonGroup>

            <ButtonGroup>
              <Button variant="outline" size="sm">
                <Zap className="h-3 w-3" />
              </Button>
              <Button variant="outline" size="sm">
                <Star className="h-3 w-3" />
              </Button>
              <Button variant="outline" size="sm">
                <Bell className="h-3 w-3" />
              </Button>
            </ButtonGroup>

            <ButtonGroup>
              <ButtonGroupText>View:</ButtonGroupText>
              <ButtonGroupSeparator />
              <Button variant="outline">List</Button>
              <Button variant="outline">Grid</Button>
              <Button variant="outline">Gallery</Button>
            </ButtonGroup>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="Vertical Button Groups"
          description="Buttons stacked vertically"
        >
          <ButtonGroup orientation="vertical">
            <Button variant="outline">Top</Button>
            <Button variant="outline">Middle</Button>
            <Button variant="outline">Bottom</Button>
          </ButtonGroup>
        </ComponentShowcase>
      </div>

      {/* Cards */}
      <div id="cards" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Cards"
          description="Container component for displaying grouped content with header, body, and footer."
          filePath="src/ui/primitives/card.tsx"
        />

        <ComponentShowcase
          title="Card Examples"
          description="Cards for organizing related content"
        >
          <VariantGrid columns={3}>
            <Card>
              <CardHeader>
                <CardTitle>Card Title</CardTitle>
                <CardDescription>Card description goes here</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  This is the card content area where you can place any content.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>With Footer</CardTitle>
                <CardDescription>Card with actions in footer</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Card content here.</p>
              </CardContent>
              <CardFooter className="gap-2">
                <Button variant="outline" size="sm">
                  Cancel
                </Button>
                <Button size="sm">Save</Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Feature Card</CardTitle>
                <CardDescription>Product feature highlight</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Fast performance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Easy to use</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Fully accessible</span>
                </div>
              </CardContent>
            </Card>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Checkboxes */}
      <div id="checkboxes" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Checkboxes"
          description="Form checkbox component for selections and multi-select."
          filePath="src/ui/primitives/checkbox.tsx"
        />

        <ComponentShowcase
          title="Checkbox States"
          description="Different checkbox states"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Unchecked">
              <Checkbox />
            </VariantItem>
            <VariantItem label="Checked">
              <Checkbox checked />
            </VariantItem>
            <VariantItem label="Disabled">
              <Checkbox disabled />
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="With Labels"
          description="Checkboxes with associated labels"
        >
          <div className="max-w-md space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="terms" />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Accept terms and conditions
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="marketing" />
              <label
                htmlFor="marketing"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Send me marketing emails
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="disabled" disabled />
              <label
                htmlFor="disabled"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Disabled checkbox
              </label>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Field */}
      <div id="field" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Field"
          description="Structured form field components with labels, descriptions, and errors."
          filePath="src/ui/primitives/field.tsx"
        />

        <ComponentShowcase
          title="Field Examples"
          description="Form fields with labels, descriptions, and error messages"
        >
          <FieldGroup className="max-w-md">
            <Field>
              <FieldLabel htmlFor="username">Username</FieldLabel>
              <Input id="username" placeholder="Enter username" />
              <FieldDescription>
                This will be your public display name.
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor="email-field">Email</FieldLabel>
              <Input
                id="email-field"
                type="email"
                placeholder="email@example.com"
              />
              <FieldDescription>
                We'll never share your email with anyone else.
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor="password-field">Password</FieldLabel>
              <Input id="password-field" type="password" />
              <FieldError
                errors={[{ message: "Password must be at least 8 characters" }]}
              />
            </Field>

            <Field orientation="horizontal">
              <FieldLabel htmlFor="notifications">Notifications</FieldLabel>
              <Switch id="notifications" />
              <FieldDescription>
                Receive email notifications about your account
              </FieldDescription>
            </Field>
          </FieldGroup>
        </ComponentShowcase>
      </div>

      {/* Empty */}
      <div id="empty" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Empty"
          description="Empty state component for no data or search results."
          filePath="src/ui/primitives/empty.tsx"
        />

        <ComponentShowcase
          title="Empty State Examples"
          description="Display friendly empty states"
        >
          <div className="space-y-6">
            <Empty className="border">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Inbox className="h-6 w-6" />
                </EmptyMedia>
                <EmptyTitle>No messages</EmptyTitle>
                <EmptyDescription>
                  You don't have any messages yet. Start a conversation!
                </EmptyDescription>
              </EmptyHeader>
              <Button>New Message</Button>
            </Empty>

            <Empty className="border">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <FileQuestion className="h-6 w-6" />
                </EmptyMedia>
                <EmptyTitle>No results found</EmptyTitle>
                <EmptyDescription>
                  Try adjusting your search or filter to find what you're
                  looking for.
                </EmptyDescription>
              </EmptyHeader>
              <Button variant="outline">Clear Filters</Button>
            </Empty>
          </div>
        </ComponentShowcase>
      </div>

      {/* Input Group */}
      <div id="input-group" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Input Group"
          description="Enhanced inputs with icons, buttons, and text addons."
          filePath="src/ui/primitives/input-group.tsx"
        />

        <ComponentShowcase
          title="Input Group Examples"
          description="Inputs with addons and actions"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search with Icon</label>
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <Search className="h-4 w-4" />
                </InputGroupAddon>
                <InputGroupInput placeholder="Search..." />
              </InputGroup>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                URL with Action Button
              </label>
              <InputGroup>
                <InputGroupInput placeholder="https://example.com" />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton>Go</InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Email with Button</label>
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <Mail className="h-4 w-4" />
                </InputGroupAddon>
                <InputGroupInput type="email" placeholder="email@example.com" />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton>Subscribe</InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Item */}
      <div id="item" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Item"
          description="Structured list item component for consistent list layouts."
          filePath="src/ui/primitives/item.tsx"
        />

        <ComponentShowcase
          title="Item Examples"
          description="List items with media, content, and actions"
        >
          <ItemGroup className="max-w-md">
            <Item>
              <ItemMedia variant="icon">
                <Mail className="h-4 w-4" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>New message from Sarah</ItemTitle>
                <ItemDescription>
                  Hey! I wanted to follow up on our conversation...
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button size="sm" variant="ghost">
                  View
                </Button>
              </ItemActions>
            </Item>
            <ItemSeparator />
            <Item>
              <ItemMedia variant="icon">
                <Bell className="h-4 w-4" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>System Update Available</ItemTitle>
                <ItemDescription>
                  A new version is ready to install
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button size="sm" variant="ghost">
                  Update
                </Button>
              </ItemActions>
            </Item>
            <ItemSeparator />
            <Item variant="muted">
              <ItemMedia variant="icon">
                <CheckCircle2 className="h-4 w-4" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>Task completed</ItemTitle>
                <ItemDescription>
                  Your report has been generated successfully
                </ItemDescription>
              </ItemContent>
            </Item>
          </ItemGroup>
        </ComponentShowcase>
      </div>

      {/* Kbd */}
      <div id="kbd" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Kbd"
          description="Display keyboard shortcuts and keys."
          filePath="src/ui/primitives/kbd.tsx"
        />

        <ComponentShowcase
          title="Kbd Examples"
          description="Show keyboard shortcuts to users"
        >
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm">Single key:</span>
                <Kbd>K</Kbd>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">With symbol:</span>
                <Kbd>⌘</Kbd>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Text key:</span>
                <Kbd>Shift</Kbd>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">Command palette:</span>
                <KbdGroup>
                  <Kbd>⌘</Kbd>
                  <Kbd>K</Kbd>
                </KbdGroup>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Save:</span>
                <KbdGroup>
                  <Kbd>⌘</Kbd>
                  <Kbd>S</Kbd>
                </KbdGroup>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Copy:</span>
                <KbdGroup>
                  <Kbd>Ctrl</Kbd>
                  <Kbd>C</Kbd>
                </KbdGroup>
              </div>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Label */}
      <div id="label" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Label"
          description="Accessible form label component."
          filePath="src/ui/primitives/label.tsx"
        />

        <ComponentShowcase
          title="Label Examples"
          description="Labels paired with form controls"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-label">Email Address</Label>
              <Input
                id="email-label"
                type="email"
                placeholder="email@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabled-label">Disabled Field</Label>
              <Input
                id="disabled-label"
                disabled
                placeholder="Disabled input"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="checkbox-label" />
              <Label htmlFor="checkbox-label">
                Accept terms and conditions
              </Label>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Native Select */}
      <div id="native-select" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Native Select"
          description="Native HTML select element with design system styling."
          filePath="src/ui/primitives/native-select.tsx"
        />

        <ComponentShowcase
          title="Native Select Examples"
          description="Simple native dropdowns (lighter than custom Select)"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <NativeSelect id="country">
                <option value="">Select a country</option>
                <option value="us">United States</option>
                <option value="ca">Canada</option>
                <option value="mx">Mexico</option>
                <option value="uk">United Kingdom</option>
              </NativeSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="size">Size</Label>
              <NativeSelect id="size" defaultValue="md">
                <option value="xs">Extra Small</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra Large</option>
              </NativeSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabled-native">Disabled</Label>
              <NativeSelect id="disabled-native" disabled>
                <option>Cannot select</option>
              </NativeSelect>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Radio Group */}
      <div id="radio-group" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Radio Group"
          description="Mutually exclusive radio button selections."
          filePath="src/ui/primitives/radio-group.tsx"
        />

        <ComponentShowcase
          title="Radio Group Examples"
          description="Single selection from multiple options"
        >
          <div className="max-w-md space-y-6">
            <div className="space-y-3">
              <Label>Notification Method</Label>
              <RadioGroup defaultValue="email">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="r-email" />
                  <Label htmlFor="r-email">Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sms" id="r-sms" />
                  <Label htmlFor="r-sms">SMS</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="push" id="r-push" />
                  <Label htmlFor="r-push">Push Notification</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label>Subscription Plan</Label>
              <RadioGroup defaultValue="pro">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="free" id="r-free" />
                  <Label htmlFor="r-free">Free</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pro" id="r-pro" />
                  <Label htmlFor="r-pro">Pro - $9/month</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="enterprise" id="r-enterprise" />
                  <Label htmlFor="r-enterprise">Enterprise - $99/month</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Scroll Area */}
      <div id="scroll-area" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Scroll Area"
          description="Custom scrollable container with styled scrollbars."
          filePath="src/ui/primitives/scroll-area.tsx"
        />

        <ComponentShowcase
          title="Vertical Scroll Area"
          description="Scrollable content with custom vertical scrollbar"
        >
          <ScrollArea className="h-72 w-48 rounded-md border">
            <div className="p-4">
              <h4 className="mb-4 text-sm font-medium leading-none">Tags</h4>
              {Array.from({ length: 50 }).map((_, i, a) => (
                <div key={i}>
                  <div className="text-sm">v1.2.0-beta.{a.length - i}</div>
                  {i < a.length - 1 && <Separator className="my-2" />}
                </div>
              ))}
            </div>
          </ScrollArea>
        </ComponentShowcase>

        <ComponentShowcase
          title="Horizontal Scroll Area"
          description="Horizontally scrollable content with images"
        >
          <ScrollArea className="w-96 whitespace-nowrap rounded-md border">
            <div className="flex w-max space-x-4 p-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-[150px] w-[200px] shrink-0 items-center justify-center rounded-md bg-muted"
                >
                  <span className="font-semibold">Item {i + 1}</span>
                </div>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </ComponentShowcase>
      </div>

      {/* Skeleton */}
      <div id="skeleton" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Skeleton"
          description="Loading skeleton placeholders with pulse animation."
          filePath="src/ui/primitives/skeleton.tsx"
        />

        <ComponentShowcase
          title="Skeleton Examples"
          description="Loading states for different content types"
        >
          <div className="max-w-md space-y-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Card Skeleton</p>
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[250px]" />
                  <Skeleton className="h-4 w-[200px]" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Text Skeleton</p>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-[90%]" />
                <Skeleton className="h-4 w-[80%]" />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Button Skeleton</p>
              <Skeleton className="h-10 w-[120px]" />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Spinner */}
      <div id="spinner" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Spinner"
          description="Animated loading spinner indicator."
          filePath="src/ui/primitives/spinner.tsx"
        />

        <ComponentShowcase
          title="Spinner Examples"
          description="Loading spinners in different sizes"
        >
          <VariantGrid columns={4}>
            <VariantItem label="Small">
              <Spinner className="h-4 w-4" />
            </VariantItem>
            <VariantItem label="Default">
              <Spinner />
            </VariantItem>
            <VariantItem label="Large">
              <Spinner className="h-8 w-8" />
            </VariantItem>
            <VariantItem label="In Button">
              <Button disabled>
                <Spinner className="mr-2 h-4 w-4" />
                Loading...
              </Button>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Slider */}
      <div id="slider" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Slider"
          description="Range slider for selecting numeric values."
          filePath="src/ui/primitives/slider.tsx"
        />

        <ComponentShowcase
          title="Slider Examples"
          description="Drag the slider to select values"
        >
          <div className="max-w-md space-y-8">
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Volume</Label>
                <span className="text-sm text-muted-foreground">50%</span>
              </div>
              <Slider defaultValue={[50]} max={100} step={1} />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Price Range</Label>
                <span className="text-sm text-muted-foreground">$20 - $80</span>
              </div>
              <Slider defaultValue={[20, 80]} max={100} step={1} />
            </div>

            <div className="space-y-3">
              <Label>Disabled</Label>
              <Slider defaultValue={[50]} max={100} step={1} disabled />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Progress */}
      <div id="progress" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Progress"
          description="Progress bar for showing task completion."
          filePath="src/ui/primitives/progress.tsx"
        />

        <ComponentShowcase
          title="Progress Examples"
          description="Different progress values and use cases"
        >
          <div className="max-w-md space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading...</span>
                <span>25%</span>
              </div>
              <Progress value={25} />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing</span>
                <span>60%</span>
              </div>
              <Progress value={60} />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Almost done</span>
                <span>90%</span>
              </div>
              <Progress value={90} />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Complete</span>
                <span>100%</span>
              </div>
              <Progress value={100} />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Toggle */}
      <div id="toggle" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Toggle"
          description="Two-state toggle button component."
          filePath="src/ui/primitives/toggle.tsx"
        />

        <ComponentShowcase
          title="Toggle Examples"
          description="Click to toggle on/off state"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Default">
              <Toggle aria-label="Toggle italic">
                <Italic className="h-4 w-4" />
              </Toggle>
            </VariantItem>
            <VariantItem label="Outline">
              <Toggle variant="outline" aria-label="Toggle bold">
                <Bold className="h-4 w-4" />
              </Toggle>
            </VariantItem>
            <VariantItem label="With Text">
              <Toggle aria-label="Toggle underline">
                <Underline className="mr-2 h-4 w-4" />
                Underline
              </Toggle>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Toggle Sizes"
          description="Different sizes for various contexts"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Small">
              <Toggle size="sm" aria-label="Toggle small">
                <Italic className="h-4 w-4" />
              </Toggle>
            </VariantItem>
            <VariantItem label="Default">
              <Toggle aria-label="Toggle default">
                <Bold className="h-4 w-4" />
              </Toggle>
            </VariantItem>
            <VariantItem label="Large">
              <Toggle size="lg" aria-label="Toggle large">
                <Underline className="h-4 w-4" />
              </Toggle>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Toggle Group */}
      <div id="toggle-group" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Toggle Group"
          description="Group of toggle buttons for single or multiple selection."
          filePath="src/ui/primitives/toggle-group.tsx"
        />

        <ComponentShowcase
          title="Single Selection"
          description="Only one can be selected at a time"
        >
          <ToggleGroup type="single">
            <ToggleGroupItem value="left" aria-label="Align left">
              <AlignLeft className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="center" aria-label="Align center">
              <AlignCenter className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="right" aria-label="Align right">
              <AlignRight className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </ComponentShowcase>

        <ComponentShowcase
          title="Multiple Selection"
          description="Multiple toggles can be active"
        >
          <ToggleGroup type="multiple">
            <ToggleGroupItem value="bold" aria-label="Toggle bold">
              <Bold className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="italic" aria-label="Toggle italic">
              <Italic className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="underline" aria-label="Toggle underline">
              <Underline className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </ComponentShowcase>
      </div>

      {/* Textarea */}
      <div id="textarea" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Textarea"
          description="Multi-line text input for longer content."
          filePath="src/ui/primitives/textarea.tsx"
        />

        <ComponentShowcase
          title="Textarea Examples"
          description="Different textarea configurations"
        >
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" placeholder="Type your message here..." />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself..."
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabled-textarea">Disabled</Label>
              <Textarea
                id="disabled-textarea"
                disabled
                placeholder="Cannot edit"
              />
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Aspect Ratio */}
      <div id="aspect-ratio" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Aspect Ratio"
          description="Utility component for maintaining consistent aspect ratios."
          filePath="src/ui/primitives/aspect-ratio.tsx"
        />

        <ComponentShowcase
          title="Common Aspect Ratios"
          description="Maintain aspect ratios for images and media"
        >
          <div className="space-y-6">
            <div>
              <p className="mb-2 text-sm text-muted-foreground">16:9 (Video)</p>
              <AspectRatio
                ratio={16 / 9}
                className="overflow-hidden rounded-md bg-muted"
              >
                <img
                  src="https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=800&dpr=2&q=80"
                  alt="Placeholder"
                  className="h-full w-full object-cover"
                />
              </AspectRatio>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-2 text-sm text-muted-foreground">
                  1:1 (Square)
                </p>
                <AspectRatio
                  ratio={1}
                  className="overflow-hidden rounded-md bg-muted"
                >
                  <img
                    src="https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=800&dpr=2&q=80"
                    alt="Placeholder"
                    className="h-full w-full object-cover"
                  />
                </AspectRatio>
              </div>
              <div>
                <p className="mb-2 text-sm text-muted-foreground">
                  4:3 (Standard)
                </p>
                <AspectRatio
                  ratio={4 / 3}
                  className="overflow-hidden rounded-md bg-muted"
                >
                  <img
                    src="https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=800&dpr=2&q=80"
                    alt="Placeholder"
                    className="h-full w-full object-cover"
                  />
                </AspectRatio>
              </div>
            </div>
          </div>
        </ComponentShowcase>
      </div>
    </div>
  );
}
